import {
    StyleFunction,
    useAuth,
    useForm,
    useThemedStyle,
} from "@venuepos/react-common";
import {
    GQApprovalTokenInput,
    useApprovalTokenLazyQuery,
    useApprovalTokenSaveMutation,
} from "graphql-sdk";
import { schemaApprovalToken } from "lib";
import { useTranslation } from "locales";
import React, { useCallback, useEffect } from "react";
import { View } from "react-native";
import { useHandleMutationError } from "../../../../hooks";
import { RootStackScreenProps } from "../../../../navigation";
import { AdminContainer } from "../../../container";
import { Form } from "../form";

type ScreenProps = RootStackScreenProps<"APPROVAL_TOKEN_EDIT">;

export function ApprovalTokenEditScreen({
    navigation: { navigate },
    route,
}: ScreenProps) {
    const auth = useAuth();
    auth.enforce("merchant.approval");

    const [t] = useTranslation();

    const [approvalTokenSave] = useApprovalTokenSaveMutation();
    const { handleMutationError } = useHandleMutationError();

    const [getApprovalToken, approvalToken] = useApprovalTokenLazyQuery({
        fetchPolicy: "no-cache",
    });
    const form = useForm<GQApprovalTokenInput>(schemaApprovalToken, null);

    const [{ values }, { setDefaultValues, reset }] = form;

    const styles = useThemedStyle(styleFunc);
    const id = route.params.id;

    useEffect(() => {
        if (!id) {
            navigate("APPROVALS");
        } else {
            getApprovalToken({ variables: { id } });
        }
    }, [getApprovalToken, navigate, id]);

    useEffect(() => {
        if (!approvalToken.data || !approvalToken.data.approvalToken) {
            return;
        }

        setDefaultValues({
            type: approvalToken.data.approvalToken.type,
            approvalTokenGroupId:
                approvalToken.data.approvalToken.approvalTokenGroupId,
            value: approvalToken.data.approvalToken.value,
            name: approvalToken.data.approvalToken.name,
            active: approvalToken.data.approvalToken.active,
        });
    }, [approvalToken.data, setDefaultValues]);

    const handleSave = useCallback(async () => {
        if (
            !approvalToken ||
            !approvalToken.data ||
            !approvalToken.data.approvalToken ||
            !values
        ) {
            return;
        }

        await handleMutationError(
            async () =>
                await approvalTokenSave({
                    variables: {
                        id: approvalToken.data!.approvalToken!.id,
                        approvalToken: {
                            name: values.name,
                            approvalTokenGroupId: values.approvalTokenGroupId,
                            value: values.value,
                            type: values.type,
                            active: values.active,
                        },
                    },
                }),
            t("common.saved", "Saved"),
            () => {
                reset();
                navigate("APPROVALS", { refetch: true, tabIndex: 1 });
            }
        );
    }, [
        approvalToken,
        values,
        handleMutationError,
        t,
        approvalTokenSave,
        reset,
        navigate,
    ]);

    return (
        <AdminContainer>
            <View>
                <View style={styles.formBox}>
                    <Form
                        form={form}
                        onSubmit={handleSave}
                        submitButton={["common.save", "Save"]}
                    />
                </View>
            </View>
        </AdminContainer>
    );
}

const styleFunc: StyleFunction = theme => ({
    formBox: {
        maxWidth: theme.dimensions.maxFormWidth,
    },
});
